/**
 * Integraciones (ADR 0053): claves del canal público de estado de las estaciones. La lista no trae el
 * hash; la clave en claro aparece una sola vez, en el panel que sigue a la creación. Crear y revocar
 * exigen `integrations:manage` (solo administradores); el resto solo ve la lista.
 */
import { type FormEvent, useState } from 'react';
import { useApi, useAuth } from '../auth/auth.tsx';
import { ConfirmDialog } from '../components/confirm-dialog.tsx';
import { DataTable } from '../components/data-table.tsx';
import { Alert, Badge, Copyable, ErrorBox, Field, Loading, PageHeader } from '../components/ui.tsx';
import { useI18n } from '../i18n/index.tsx';
import { dateTime } from '../lib/format.ts';
import type { IntegrationKey, Items } from '../lib/types.ts';
import { useMutation, useQuery } from '../lib/use-query.ts';

const MAX_ORIGINS = 10;

/** Orígenes del formulario: uno por línea, sin vacíos ni repetidos (la API los normaliza y valida). */
export function parseOrigins(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0),
    ),
  ];
}

export function IntegrationsPage() {
  const { t, locale } = useI18n();
  const api = useApi();
  const auth = useAuth();
  const keys = useQuery(() => api.get<Items<IntegrationKey>>('/integrations/keys'), []);
  const mutation = useMutation();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ label: '', origins: '' });
  const [issued, setIssued] = useState<{ key: string; item: IntegrationKey } | null>(null);
  const [revoking, setRevoking] = useState<IntegrationKey | null>(null);
  const canManage = auth.can('integrations:manage');
  const origins = parseOrigins(form.origins);

  const create = (event: FormEvent) => {
    event.preventDefault();
    void mutation
      .run(() =>
        api.post<{ key: string; item: IntegrationKey }>('/integrations/keys', {
          label: form.label.trim(),
          ...(origins.length ? { allowedOrigins: origins } : {}),
        }),
      )
      .then((created) => {
        if (created) {
          setIssued(created);
          setCreating(false);
          setForm({ label: '', origins: '' });
          void keys.reload();
        }
      });
  };

  return (
    <>
      <PageHeader
        title={t('integrations.title')}
        subtitle={t('integrations.subtitle')}
        actions={
          canManage ? (
            <button type="button" className="primary" onClick={() => setCreating((v) => !v)}>
              {t('integrations.create')}
            </button>
          ) : null
        }
      />
      <ErrorBox error={mutation.error} />
      {mutation.message ? <Alert tone="ok">{mutation.message}</Alert> : null}
      {issued ? (
        <Alert tone="warning" icon="vpn-key">
          <strong>{t('integrations.issuedTitle', { label: issued.item.label })}</strong>
          <p className="mt">{t('integrations.issuedHelp')}</p>
          <p>
            <Copyable value={issued.key} />
          </p>
          <p className="small">
            {issued.item.allowedOrigins.length
              ? `${t('integrations.origins')}: ${issued.item.allowedOrigins.join(', ')}`
              : t('integrations.anyOrigin')}
          </p>
          <button type="button" className="small" onClick={() => setIssued(null)}>
            {t('app.close')}
          </button>
        </Alert>
      ) : null}
      {creating ? (
        <form className="card" onSubmit={create}>
          <div className="grid cols-2">
            <Field label={t('integrations.label')} help={t('integrations.labelHelp')}>
              <input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                maxLength={80}
                required
              />
            </Field>
            <Field label={t('integrations.origins')} help={t('integrations.originsHelp')}>
              <textarea
                value={form.origins}
                onChange={(e) => setForm({ ...form, origins: e.target.value })}
                rows={4}
                placeholder={'https://supercargadores.co\nhttps://www.supercargadores.co'}
              />
            </Field>
          </div>
          <Alert tone="info">{t('integrations.publicWarning')}</Alert>
          <div className="form-actions">
            <button type="button" onClick={() => setCreating(false)}>
              {t('app.cancel')}
            </button>
            <button
              type="submit"
              className="primary"
              disabled={mutation.busy || origins.length > MAX_ORIGINS}
            >
              {t('integrations.create')}
            </button>
          </div>
        </form>
      ) : null}
      <div className="card">
        <ErrorBox error={keys.error} />
        {keys.loading ? <Loading /> : null}
        <DataTable
          rows={keys.data?.items ?? []}
          rowKey={(k) => k.id}
          columns={[
            {
              key: 'label',
              header: t('integrations.label'),
              render: (k) => (
                <>
                  <strong>{k.label}</strong>
                  <div className="small muted">
                    {t('app.created')}: {dateTime(k.createdAt, locale)} · {k.createdBy}
                  </div>
                </>
              ),
            },
            {
              key: 'prefix',
              header: t('integrations.prefix'),
              render: (k) => <code>{k.keyPrefix}…</code>,
            },
            {
              key: 'origins',
              header: t('integrations.origins'),
              render: (k) =>
                k.allowedOrigins.length ? (
                  k.allowedOrigins.map((origin) => <div key={origin}>{origin}</div>)
                ) : (
                  <span className="muted">{t('integrations.anyOrigin')}</span>
                ),
            },
            {
              key: 'lastUsed',
              header: t('integrations.lastUsed'),
              render: (k) =>
                k.lastUsedAt ? dateTime(k.lastUsedAt, locale) : t('integrations.neverUsed'),
            },
            {
              key: 'status',
              header: t('app.status'),
              render: (k) =>
                k.revokedAt ? (
                  <>
                    <Badge tone="danger">{t('integrations.revoked')}</Badge>
                    <div className="small muted">
                      {dateTime(k.revokedAt, locale)} · {k.revokedBy}
                      {k.revokeReason ? ` · ${k.revokeReason}` : ''}
                    </div>
                  </>
                ) : (
                  <Badge tone="ok">{t('integrations.active')}</Badge>
                ),
            },
            {
              key: 'act',
              header: '',
              render: (k) =>
                canManage && !k.revokedAt ? (
                  <button type="button" className="small" onClick={() => setRevoking(k)}>
                    {t('integrations.revoke')}
                  </button>
                ) : null,
            },
          ]}
        />
      </div>
      <ConfirmDialog
        open={revoking !== null}
        title={`${t('integrations.revoke')}: ${revoking?.label ?? ''}`}
        message={t('integrations.revokeHelp')}
        danger
        requireReason
        busy={mutation.busy}
        onCancel={() => setRevoking(null)}
        onConfirm={async (reason) => {
          const target = revoking;
          setRevoking(null);
          if (target) {
            await mutation.run(
              () => api.post(`/integrations/keys/${target.id}/revoke`, { reason }),
              t('integrations.revokedMessage'),
            );
            void keys.reload();
          }
        }}
      />
    </>
  );
}
